import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const DEFAULT_COLORS = ['#4F81BD', '#C0504D', '#9BBB59', '#8064A2', '#F79646', '#4BACC6', '#F2C811', '#7F7F7F', '#92A9CF', '#D99694'];
const RADIAN = Math.PI / 180;

function chartValue(row, key) {
  return Number(row?.[key] || 0);
}

function truncateLabel(value, max = 24) {
  const text = String(value ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function shouldShowCallout(payload, fallbackPercent, minPercent, maxCallouts) {
  if (!payload) return false;
  if (payload.__forceCallout) return true;
  const percent = Number(payload.__percent ?? fallbackPercent ?? 0);
  if (!percent || percent < minPercent) return false;
  return Number(payload.__calloutRank ?? 999) <= maxCallouts;
}

function evenlySpacedOffsets(count) {
  if (count <= 1) return [0];
  const range = Math.min(1.16, Math.max(0.82, count * 0.16));
  const start = -range;
  const step = (range * 2) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + step * index);
}

function addPieCalloutLayout(rows, total, forceEveryCallout, minPercent = 0, maxCallouts = 999) {
  let cumulative = 0;
  const startAngle = 90;
  const withAngles = rows.map((row) => {
    const percent = total ? row.__value / total : 0;
    const sweep = percent * 360;
    const midAngle = startAngle - cumulative - sweep / 2;
    cumulative += sweep;
    const cos = Math.cos(-RADIAN * midAngle);
    const sin = Math.sin(-RADIAN * midAngle);
    return {
      ...row,
      __computedMidAngle: midAngle,
      __computedSide: cos >= 0 ? 'right' : 'left',
      __computedY: sin,
      __forceCallout: forceEveryCallout,
    };
  });

  ['left', 'right'].forEach((side) => {
    const sideRows = withAngles
      .filter((row) => row.__computedSide === side)
      .filter((row) => forceEveryCallout || (Number(row.__percent || 0) >= minPercent && Number(row.__calloutRank || 999) <= maxCallouts))
      .sort((a, b) => a.__computedY - b.__computedY);
    const offsets = evenlySpacedOffsets(sideRows.length);
    sideRows.forEach((row, index) => {
      row.__calloutOffset = offsets[index] ?? 0;
      row.__calloutLane = index;
      row.__calloutLaneCount = sideRows.length;
    });
  });

  return withAngles;
}


function buildPieLegendRows(rows, nameKey, colors, total) {
  return rows.map((row, index) => {
    const safeValue = Number(row.__value || 0);
    const percent = total ? safeValue / total : 0;
    return {
      name: row[nameKey],
      value: safeValue,
      percent,
      color: colors[index % colors.length],
    };
  });
}

function DonutLegend({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="donut-detail-legend">
      {rows.map((row, index) => (
        <div className="donut-legend-item" key={`${row.name}-${index}`}>
          <span className="donut-legend-swatch" style={{ background: row.color }} />
          <span className="donut-legend-name" title={row.name}>{row.name}</span>
          <span className="donut-legend-value">{row.value.toLocaleString()} ({(row.percent * 100).toFixed(0)}%)</span>
        </div>
      ))}
    </div>
  );
}

function renderSliceCallout({ cx, cy, midAngle, outerRadius, name, value, percent, payload, minPercent, maxCallouts, includeInsideCount = false, isDonut = false }) {
  if (!shouldShowCallout(payload, percent, minPercent, maxCallouts)) return null;

  const displayMidAngle = Number.isFinite(payload?.__computedMidAngle) ? payload.__computedMidAngle : midAngle;
  const sin = Math.sin(-RADIAN * displayMidAngle);
  const cos = Math.cos(-RADIAN * displayMidAngle);
  const side = payload?.__computedSide === 'left' ? -1 : payload?.__computedSide === 'right' ? 1 : (cos >= 0 ? 1 : -1);
  const sx = cx + (outerRadius + 4) * cos;
  const sy = cy + (outerRadius + 4) * sin;
  const spread = isDonut ? Math.max(outerRadius + 26, 96) : Math.max(outerRadius + 30, 82);
  const targetY = Number.isFinite(payload?.__calloutOffset)
    ? cy + payload.__calloutOffset * spread
    : cy + (outerRadius + 24) * sin;
  const mx = cx + side * (outerRadius + 18);
  const my = targetY;
  const ex = cx + side * (outerRadius + (isDonut ? 122 : 104));
  const ey = targetY;
  // Keep callout text inside the chart box: text extends back toward the donut/pie, not outward.
  const textAnchor = side >= 0 ? 'end' : 'start';
  const textX = ex - side * 6;
  const percentText = `${(Number(percent || payload?.__percent || 0) * 100).toFixed(0)}%`;
  const displayValue = Number(value || payload?.__value || 0).toLocaleString();
  const labelName = truncateLabel(name ?? payload?.name ?? payload?.service_type ?? payload?.status ?? '', isDonut ? 22 : 24);

  const insideRadius = Math.max(18, outerRadius * 0.55);
  const insideX = cx + insideRadius * cos;
  const insideY = cy + insideRadius * sin;

  return (
    <g className="chart-callout-label">
      {includeInsideCount && Number(value || payload?.__value || 0) > 0 && (
        <text
          x={insideX}
          y={insideY}
          fill="#ffffff"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={12}
          fontWeight={900}
          paintOrder="stroke"
          stroke="#111827"
          strokeWidth={3}
        >
          {displayValue}
        </text>
      )}
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#64748b" fill="none" strokeWidth={1.1} />
      <circle cx={sx} cy={sy} r={2} fill="#64748b" />
      <text x={textX} y={ey - 4} textAnchor={textAnchor} fontSize={11} fill="#334155" fontWeight={800}>
        {labelName}
      </text>
      <text x={textX} y={ey + 12} textAnchor={textAnchor} fontSize={11} fill="#334155">
        {displayValue} ({percentText})
      </text>
    </g>
  );
}

export default function ChartCard({
  title,
  data,
  xKey,
  yKey = 'project_count',
  height = 300,
  type = 'bar',
  series,
  layout = 'vertical',
  nameKey,
  valueKey,
  showLegend = true,
  minCalloutPercent,
  maxCallouts,
}) {
  const chartData = data || [];
  const stackSeries = series || [{ key: yKey, name: 'Projects' }];
  const colors = DEFAULT_COLORS;
  const manyCategories = chartData.length > 18;

  function renderChart() {
    if (!chartData.length) return <div className="chart-empty">No chart data</div>;

    if (type === 'donut' || type === 'pie') {
      const resolvedNameKey = nameKey || xKey;
      const resolvedValueKey = valueKey || yKey;
      const total = chartData.reduce((sum, row) => sum + chartValue(row, resolvedValueKey), 0);
      const preparedData = chartData.map((row, index) => ({
        ...row,
        __index: index,
        __value: chartValue(row, resolvedValueKey),
        __percent: total ? chartValue(row, resolvedValueKey) / total : 0,
      }));
      const rankedData = [...preparedData].sort((a, b) => b.__value - a.__value);
      rankedData.forEach((row, index) => { row.__calloutRank = index + 1; });
      const rankByIndex = new Map(rankedData.map((row) => [row.__index, row.__calloutRank]));
      const rankedPreparedData = preparedData.map((row) => ({ ...row, __calloutRank: rankByIndex.get(row.__index) || 999 }));
      const donutOuterRadius = 84;
      const pieOuterRadius = 92;
      const resolvedMinCalloutPercent = minCalloutPercent ?? (type === 'donut' ? 0.03 : 0.01);
      const resolvedMaxCallouts = maxCallouts ?? (type === 'donut' ? 3 : 8);
      const displayData = addPieCalloutLayout(rankedPreparedData, total, false, resolvedMinCalloutPercent, resolvedMaxCallouts);
      const legendRows = buildPieLegendRows(displayData, resolvedNameKey, colors, total);
      const pieMargin = type === 'donut'
        ? { top: 34, right: 150, bottom: 12, left: 150 }
        : { top: 36, right: 126, bottom: showLegend ? 64 : 46, left: 126 };

      const pieChart = (
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <PieChart margin={pieMargin}>
            <Pie
              data={displayData}
              dataKey={resolvedValueKey}
              nameKey={resolvedNameKey}
              innerRadius={type === 'donut' ? 48 : 0}
              outerRadius={type === 'donut' ? donutOuterRadius : pieOuterRadius}
              startAngle={90}
              endAngle={-270}
              label={(props) => renderSliceCallout({
                ...props,
                minPercent: resolvedMinCalloutPercent,
                maxCallouts: resolvedMaxCallouts,
                includeInsideCount: type === 'pie',
                isDonut: type === 'donut',
              })}
              labelLine={false}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {displayData.map((entry, index) => <Cell key={`${entry[resolvedNameKey]}-${index}`} fill={colors[index % colors.length]} />)}
            </Pie>
            {type === 'donut' && (
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="donut-total-label">
                {total.toLocaleString()}
              </text>
            )}
            <Tooltip formatter={(value, name) => [Number(value || 0).toLocaleString(), name]} />
            {type !== 'donut' && showLegend && (
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ fontSize: 11, lineHeight: '16px', paddingTop: 8 }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      );

      if (type === 'donut') {
        return (
          <div className="donut-chart-layout">
            <div className="donut-chart-canvas">{pieChart}</div>
            {showLegend && <DonutLegend rows={legendRows} />}
          </div>
        );
      }

      return pieChart;
    }

    if (type === 'horizontalBar') {
      const longestLabel = chartData.reduce((max, row) => Math.max(max, String(row[xKey] || '').length), 0);
      const dynamicLeft = Math.min(300, Math.max(150, longestLabel * 5.2));
      const valueCount = chartData.length;
      const tickSize = valueCount > 35 ? 9 : valueCount > 24 ? 10 : 11;
      const barSize = valueCount > 35 ? 10 : valueCount > 24 ? 13 : 18;
      const labelMax = valueCount > 35 ? 30 : 38;
      const hasNegative = chartData.some((row) => chartValue(row, yKey) < 0);
      return (
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <BarChart
            data={chartData}
            layout="vertical"
            barCategoryGap={valueCount > 30 ? 3 : 6}
            margin={{ top: 18, right: 86, bottom: 18, left: dynamicLeft }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} domain={hasNegative ? ['auto', 'auto'] : [0, 'auto']} />
            <YAxis
              dataKey={xKey}
              type="category"
              interval={0}
              width={dynamicLeft - 12}
              tick={{ fontSize: tickSize }}
              tickFormatter={(value) => truncateLabel(value, labelMax)}
            />
            <Tooltip />
            <Bar dataKey={yKey} name="Projects" fill={colors[0]} barSize={barSize} maxBarSize={22} minPointSize={2} isAnimationActive={false}>
              <LabelList
                dataKey={(row) => {
                  const value = chartValue(row, yKey);
                  if (value === 0) return '';
                  return Number.isInteger(value) ? value : value.toFixed(2);
                }}
                position="right"
                fill="#334155"
                fontSize={tickSize}
                fontWeight={800}
                paintOrder="stroke"
                stroke="#ffffff"
                strokeWidth={2.5}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'stackedBar') {
      return (
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <BarChart data={chartData} margin={{ top: 26, right: 44, bottom: manyCategories ? 116 : 82, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={xKey} angle={-40} textAnchor="end" interval={0} height={manyCategories ? 124 : 92} tick={{ fontSize: manyCategories ? 9 : 10 }} tickFormatter={(value) => truncateLabel(value, manyCategories ? 14 : 18)} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            {showLegend && <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />}
            {stackSeries.map((item, index) => (
              <Bar key={item.key} dataKey={item.key} name={item.name} stackId="milestones" fill={colors[index % colors.length]} maxBarSize={42} isAnimationActive={false}>
                <LabelList
                  dataKey={(row) => (chartValue(row, item.key) ? chartValue(row, item.key) : '')}
                  position="center"
                  fontSize={10}
                  fill="#ffffff"
                  fontWeight={800}
                  stroke="#1f2937"
                  strokeWidth={2}
                  paintOrder="stroke"
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'groupedBar') {
      return (
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <BarChart data={chartData} margin={{ top: 28, right: 44, bottom: 72, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={xKey} angle={layout === 'vertical' ? -28 : 0} textAnchor={layout === 'vertical' ? 'end' : 'middle'} interval={0} height={76} tickFormatter={(value) => truncateLabel(value, 22)} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            {showLegend && <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />}
            {stackSeries.map((item, index) => (
              <Bar key={item.key} dataKey={item.key} name={item.name} fill={colors[index % colors.length]} maxBarSize={54} isAnimationActive={false}>
                <LabelList
                  dataKey={(row) => {
                    const value = chartValue(row, item.key);
                    if (!value) return '';
                    return Number.isInteger(value) ? value : value.toFixed(2);
                  }}
                  position="top"
                  fontSize={10}
                  fill="#111827"
                  fontWeight={800}
                  paintOrder="stroke"
                  stroke="#ffffff"
                  strokeWidth={2.5}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%" debounce={80}>
        <BarChart data={chartData} margin={{ top: 28, right: 44, bottom: manyCategories ? 110 : 70, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={xKey} angle={-35} textAnchor="end" interval={0} height={manyCategories ? 118 : 78} tick={{ fontSize: manyCategories ? 9 : 11 }} tickFormatter={(value) => truncateLabel(value, manyCategories ? 14 : 20)} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey={yKey} name="Projects" fill={colors[0]} maxBarSize={42} isAnimationActive={false}>
            <LabelList dataKey={yKey} position="top" fill="#334155" fontSize={10} fontWeight={700} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const effectiveHeight = type === 'horizontalBar'
    ? Math.max(height, Math.min(2200, chartData.length * 24 + 110))
    : height;

  return (
    <div className={`panel chart-panel chart-panel-${type}`}>
      <div className="panel-header">
        <h3>{title}</h3>
      </div>
      <div className="chart-inner" style={{ height: effectiveHeight }}>
        {renderChart()}
      </div>
    </div>
  );
}
